import React, { useCallback, useEffect, useState } from 'react';
import * as api from '@/api/client';
import {
  ArrowLeft, Save, RefreshCw, RotateCcw, Download,
  Loader2, CheckCircle, AlertTriangle, ChevronDown, ChevronRight,
  Sparkles, X,
} from 'lucide-react';
import AppFooter from '@/components/layout/AppFooter';

/**
 * SkillStudio — admin-only surface for managing the
 * ARCTIC_DRAFTING_SKILL_DOCUMENT. Mirrors the PromptAdmin editor
 * pattern (plain <textarea>, flash messages, confirm dialogs) so
 * the UX stays consistent across admin tools.
 *
 * Four vertical sections:
 *   1. Status bar        — enable toggle + load/index chip
 *   2. Section map       — per-section detection pills + actions
 *   3. Inline editor     — textarea with Save / Reset
 *   4. Usage info        — coming soon
 */
export default function SkillStudio({ onBack }: { onBack: () => void }) {
  const [doc, setDoc] = useState<api.SkillDocResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const [reindexing, setReindexing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [syncResult, setSyncResult] = useState<api.SkillSyncPromptsResponse | null>(null);
  const [confirmSync, setConfirmSync] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const [sectionMapOpen, setSectionMapOpen] = useState(true);
  const [usageOpen, setUsageOpen] = useState(false);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const data = await api.getSkillDocument();
      setDoc(data);
      setDraft(data.content ?? '');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load skill document');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const showFlash = (msg: string, ms = 4000) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), ms);
  };

  const handleToggleEnabled = async () => {
    if (!doc) return;
    const next = !doc.skill_guidance_enabled;
    setTogglingEnabled(true);
    setError(null);
    try {
      await api.setSkillEnabled(next);
      setDoc({ ...doc, skill_guidance_enabled: next });
      showFlash(next ? 'Skill guidance enabled.' : 'Skill guidance disabled.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to toggle skill guidance');
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await api.putSkillDocument(draft);
      setDoc(data);
      setDraft(data.content ?? '');
      showFlash('Saved — document indexed status reset. Click Re-index to rebuild the search index.');
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (e?.message || 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    setError(null);
    try {
      const result = await api.reindexSkillDocument();
      if (result.status === 'ok') {
        showFlash(`Re-indexed — ${result.node_count ?? 0} nodes in the tree.`);
        await reload();
      } else {
        setError(result.message || 'Re-index failed');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Re-index failed');
    } finally {
      setReindexing(false);
    }
  };

  const runSyncPrompts = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    try {
      const result = await api.syncSkillPrompts();
      setSyncResult(result);
      showFlash(
        `${result.synced.length} prompt(s) restored to their skill-aware seed.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
      setConfirmSync(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setError(null);
    try {
      const data = await api.resetSkillDocument();
      setDoc(data);
      setDraft(data.content ?? '');
      showFlash('Reset to the seed version from the repo.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  };

  const statusChip = () => {
    if (!doc || !doc.exists) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-red-50 text-red-700 text-xs font-medium border border-red-200">
          <AlertTriangle size={12} /> Not loaded
        </span>
      );
    }
    if (doc.indexed && doc.indexed_is_stale) {
      return (
        <span
          title="The document has been edited since the last index. Re-index to refresh."
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200"
        >
          <AlertTriangle size={12} /> Index stale — re-index
        </span>
      );
    }
    if (doc.indexed) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-green-50 text-green-700 text-xs font-medium border border-green-200">
          <CheckCircle size={12} /> Indexed — {doc.pageindex_node_count} nodes
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
        <AlertTriangle size={12} /> Loaded — not indexed
      </span>
    );
  };

  const pill = (ok: boolean, label: string) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs border ${
        ok
          ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-zinc-50 text-zinc-500 border-zinc-200'
      }`}
    >
      {ok ? <CheckCircle size={11} /> : <X size={11} />}
      {label}
    </span>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-zinc-500">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading skill document…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            data-testid="skill-back-button"
            onClick={onBack}
            className="text-zinc-500 hover:text-zinc-900 transition-colors p-1"
            title="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
              <Sparkles size={16} className="text-[#002FA7]" />
              Skill Studio
            </h1>
            <p className="text-xs text-zinc-500">
              Manage the ARCTIC_DRAFTING_SKILL_DOCUMENT used for Stage 2/3/4 guidance
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {flash && (
          <div
            data-testid="skill-flash"
            className="bg-green-50 border border-green-200 text-green-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2"
          >
            <CheckCircle size={14} /> {flash}
          </div>
        )}
        {error && (
          <div
            data-testid="skill-error"
            className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded-sm flex items-center gap-2"
          >
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* ─── 1. STATUS BAR ─────────────────────────────────────── */}
        <section
          data-testid="skill-status-bar"
          className="border border-zinc-200 rounded-sm p-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="skill-enabled-toggle"
                checked={!!doc?.skill_guidance_enabled}
                onChange={handleToggleEnabled}
                disabled={togglingEnabled}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-zinc-900">Skill guidance</span>
            </label>
            <span
              className={`text-xs font-medium ${
                doc?.skill_guidance_enabled ? 'text-green-700' : 'text-zinc-500'
              }`}
            >
              {doc?.skill_guidance_enabled ? 'Active' : 'Inactive'}
            </span>
            {togglingEnabled && <Loader2 size={12} className="animate-spin text-zinc-400" />}
          </div>
          <div className="flex items-center gap-3">
            {statusChip()}
          </div>
        </section>

        {/* ─── 2. SECTION MAP ────────────────────────────────────── */}
        <section data-testid="skill-section-map" className="border border-zinc-200 rounded-sm">
          <button
            onClick={() => setSectionMapOpen(!sectionMapOpen)}
            className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-zinc-900 hover:bg-zinc-50"
          >
            <span className="flex items-center gap-2">
              {sectionMapOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              Sections detected
            </span>
            <span className="text-xs text-zinc-500">
              {doc?.word_count ?? 0} words
            </span>
          </button>
          {sectionMapOpen && (
            <div className="px-4 pb-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {pill(!!doc?.sections_detected.part1, 'Part 1 — General')}
                {pill(!!doc?.sections_detected.T1_SOFTWARE, 'T1 — Software')}
                {pill(!!doc?.sections_detected.T2_MEDTECH, 'T2 — MedTech')}
                {pill(!!doc?.sections_detected.T3_MECHANICAL, 'T3 — Mechanical')}
                {pill(!!doc?.sections_detected.T4_CHEMISTRY, 'T4 — Chemistry')}
                {pill(!!doc?.sections_detected.T5_ELECTRONICS, 'T5 — Electronics')}
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
                <button
                  data-testid="skill-reindex-btn"
                  onClick={handleReindex}
                  disabled={reindexing || !doc?.exists}
                  className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {reindexing
                    ? <Loader2 size={14} className="animate-spin" />
                    : <RefreshCw size={14} />}
                  {reindexing ? 'Re-indexing…' : 'Re-index document'}
                </button>
                <button
                  data-testid="skill-sync-btn"
                  onClick={() => setConfirmSync(true)}
                  disabled={syncing || !doc?.skill_guidance_enabled}
                  title={
                    doc?.skill_guidance_enabled
                      ? 'Restore skill-aware prompts to their seed content'
                      : 'Enable skill guidance first'
                  }
                  className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {syncing
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Download size={14} />}
                  Sync skill-aware prompts
                </button>
              </div>
              {syncResult && (
                <div
                  data-testid="skill-sync-result"
                  className="mt-2 text-xs border border-zinc-200 rounded-sm p-3 bg-zinc-50"
                >
                  <div className="font-medium mb-1">Sync result</div>
                  <div><span className="text-green-700">Synced ({syncResult.synced.length}):</span> {syncResult.synced.join(', ') || '—'}</div>
                  <div><span className="text-zinc-500">Already up-to-date ({syncResult.skipped.length}):</span> {syncResult.skipped.join(', ') || '—'}</div>
                  {syncResult.not_in_seed.length > 0 && (
                    <div><span className="text-amber-700">No seed content ({syncResult.not_in_seed.length}):</span> {syncResult.not_in_seed.join(', ')}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ─── 3. INLINE EDITOR ───────────────────────────────────── */}
        <section data-testid="skill-editor" className="border border-zinc-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-zinc-900">Skill document</span>
            {doc?.version_hash && (
              <span className="text-xs text-zinc-500 font-mono">
                v{doc.version_hash}
                {doc.indexed_at ? ` · indexed ${new Date(doc.indexed_at).toLocaleString()}` : ''}
              </span>
            )}
          </div>
          <textarea
            data-testid="skill-editor-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={28}
            spellCheck={false}
            className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] resize-y"
          />
          <div className="mt-3 flex gap-2 items-center">
            <button
              data-testid="skill-save-btn"
              onClick={handleSave}
              disabled={saving || draft === (doc?.content ?? '')}
              className="bg-[#002FA7] text-white hover:bg-[#001F70] rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              data-testid="skill-reset-btn"
              onClick={() => setConfirmReset(true)}
              disabled={resetting}
              className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
              title="Restore from repo seed"
            >
              <RotateCcw size={14} />
              Reset to seed
            </button>
            {draft !== (doc?.content ?? '') && (
              <span className="text-xs text-amber-600 ml-1">Unsaved changes</span>
            )}
          </div>
        </section>

        {/* ─── 4. USAGE INFO ──────────────────────────────────────── */}
        <section data-testid="skill-usage-info" className="border border-zinc-200 rounded-sm">
          <button
            onClick={() => setUsageOpen(!usageOpen)}
            className="w-full px-4 py-3 flex items-center gap-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50"
          >
            {usageOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Usage info
          </button>
          {usageOpen && (
            <div className="px-4 pb-4 text-xs text-zinc-500">
              Per-matter usage stats coming soon. Run a matter through Stage 2/3/4
              and check its admin diagnostic export — the <code>skill_guidance</code>
              block reports the active bucket, guidance char count, and how many
              LLM calls went out with an empty placeholder.
            </div>
          )}
        </section>
      </main>

      {/* ── Confirm dialogs ── */}
      {confirmSync && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm border border-zinc-200 max-w-md w-full p-5 shadow-lg">
            <h2 className="text-sm font-medium text-zinc-900 mb-2">Sync skill-aware prompts?</h2>
            <p className="text-xs text-zinc-600 mb-4">
              This restores every prompt that declares <code>skill_guidance</code> in
              its optional placeholders to the seed (v2) content shipped with this
              build. Admin edits to those prompt templates will be overwritten.
              Prompts that already match the seed are skipped.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmSync(false)}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                data-testid="skill-sync-confirm-btn"
                onClick={runSyncPrompts}
                disabled={syncing}
                className="px-3 py-1.5 text-sm bg-[#002FA7] text-white rounded-sm hover:bg-[#001F70] disabled:opacity-50 flex items-center gap-2"
              >
                {syncing && <Loader2 size={12} className="animate-spin" />}
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmReset && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm border border-zinc-200 max-w-md w-full p-5 shadow-lg">
            <h2 className="text-sm font-medium text-zinc-900 mb-2">Reset to seed?</h2>
            <p className="text-xs text-zinc-600 mb-4">
              This discards your edits and re-imports the ARCTIC_DRAFTING_SKILL_DOCUMENT
              from the version shipped in this repo. The PageIndex tree will be cleared —
              run Re-index afterwards to rebuild it.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmReset(false)}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                data-testid="skill-reset-confirm-btn"
                onClick={handleReset}
                disabled={resetting}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              >
                {resetting && <Loader2 size={12} className="animate-spin" />}
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
      <AppFooter />
    </div>
  );
}
